import PublicRoute from './PublicRoute';
import ProtectedRoute from './ProtectedRoute';
import { ScopeGuard } from './ScopeGuard.tsx';
import { AuthorizedLayout, UnauthorizedLayout } from '@/app/layouts';
import {
  LoginForm,
  LoginMfaForm,
  LoginMfaMethodsList,
  LoginPasswordRecoveryConfirmForm,
  LoginPasswordRecoveryForm,
  LoginPasswordResetForm,
} from '@/features/auth-login';
import { MainPage } from '@/pages/main';
import { ExchangePage } from '@/pages/exchange';
import { lazy, Suspense } from 'react';
import { Loader } from '@/shared/components';
import { HistoryPage } from '@/pages/history';
import { NotFoundPage } from '@/pages/not-found';
import { ErrorBoundary } from '../providers/ErrorBoundary.tsx';
import { LocaleGuard } from '@/app/routing/LocaleGuard.tsx';
import CommonRoute from '@/app/routing/CommonRoute.tsx';

const OtcPage = lazy(() =>
  import('@/pages/otc').then(module => ({
    default: module.OtcPage,
  }))
);

const SignupPage = lazy(() =>
  import('@/pages/auth').then(module => ({
    default: module.SignupPage,
  }))
);

const SignupEulaForm = lazy(() =>
  import('@/features/auth-signup').then(module => ({
    default: module.SignupEulaForm,
  }))
);

const SignupConfirmIdentityForm = lazy(() =>
  import('@/features/auth-signup').then(module => ({
    default: module.SignupConfirmIdentityForm,
  }))
);

const SignupPhoneForm = lazy(() =>
  import('@/features/auth-signup').then(module => ({
    default: module.SignupPhoneForm,
  }))
);

const SignupNamePasswordForm = lazy(() =>
  import('@/features/auth-signup').then(module => ({
    default: module.SignupNamePasswordForm,
  }))
);

const LOGIN_CHILDREN = [
  {
    index: true,
    element: <LoginForm />,
  },
  {
    path: 'mfa-methods',
    element: <LoginMfaMethodsList />,
  },
  {
    path: 'mfa-code',
    element: <LoginMfaForm />,
  },
  {
    path: 'password-recovery',
    element: <LoginPasswordRecoveryForm />,
  },
  {
    path: 'password-recovery-confirmation',
    element: <LoginPasswordRecoveryConfirmForm />,
  },
  {
    path: 'set-new-password',
    element: <LoginPasswordResetForm />,
  },
];

const REGISTRATION_CHILDREN = [
  {
    index: true,
    element: <Loader />,
  },
  {
    path: 'eula',
    element: (
      <Suspense fallback={<Loader />}>
        <SignupEulaForm />
      </Suspense>
    ),
  },
  {
    path: 'email',
    element: (
      <Suspense fallback={<Loader />}>
        <SignupEulaForm />
      </Suspense>
    ),
  },
  {
    path: 'email-confirmation',
    element: (
      <Suspense fallback={<Loader />}>
        <SignupConfirmIdentityForm />
      </Suspense>
    ),
  },
  {
    path: 'phone',
    element: (
      <Suspense fallback={<Loader />}>
        <SignupPhoneForm />
      </Suspense>
    ),
  },
  {
    path: 'phone-confirmation',
    element: (
      <Suspense fallback={<Loader />}>
        <SignupConfirmIdentityForm />
      </Suspense>
    ),
  },
  {
    path: 'create',
    element: (
      <Suspense fallback={<Loader />}>
        <SignupNamePasswordForm />
      </Suspense>
    ),
  },
];

export default [
  {
    element: <CommonRoute />,
    children: [
      {
        element: <PublicRoute />,
        children: [
          {
            element: <UnauthorizedLayout />,
            errorElement: <ErrorBoundary homeLink="/login" />,
            children: [
              {
                path: '/:locale',
                element: <LocaleGuard />,
                children: [
                  {
                    index: true,
                    element: <LoginForm />,
                  },
                  {
                    path: 'Login',
                    children: LOGIN_CHILDREN,
                  },
                  {
                    path: 'Registration',
                    element: (
                      <Suspense fallback={<Loader />}>
                        <SignupPage />
                      </Suspense>
                    ),
                    children: REGISTRATION_CHILDREN,
                  },
                  {
                    path: 'signup',
                    element: (
                      <Suspense fallback={<Loader />}>
                        <SignupPage />
                      </Suspense>
                    ),
                    children: REGISTRATION_CHILDREN,
                  },
                ],
              },
              {
                path: '/login',
                children: LOGIN_CHILDREN,
              },
              {
                path: '/Registration',
                element: (
                  <Suspense fallback={<Loader />}>
                    <SignupPage />
                  </Suspense>
                ),
                children: REGISTRATION_CHILDREN,
              },
              {
                path: '/signup',
                element: (
                  <Suspense fallback={<Loader />}>
                    <SignupPage />
                  </Suspense>
                ),
                children: REGISTRATION_CHILDREN,
              },
            ],
          },
        ],
      },
      {
        element: <ProtectedRoute />,
        children: [
          {
            path: '/:lang?',
            element: <AuthorizedLayout />,
            errorElement: <ErrorBoundary homeLink="/" />,
            children: [
              { index: true, element: <MainPage /> },
              {
                path: 'exchange',
                element: (
                  <Suspense fallback={<Loader />}>
                    <ExchangePage />
                  </Suspense>
                ),
              },
              {
                path: 'otc',
                element: (
                  <Suspense fallback={<Loader />}>
                    <ScopeGuard scopePath="exchange.otc">
                      <OtcPage />
                    </ScopeGuard>
                  </Suspense>
                ),
              },
              // {
              //   path: 'markets',
              //   element: (
              //     <Suspense fallback={<Loader />}>
              //       <MarketsPage />
              //     </Suspense>
              //   ),
              // },
              {
                path: 'history',
                element: (
                  <Suspense fallback={<Loader />}>
                    <HistoryPage />
                  </Suspense>
                ),
                children: [
                  {
                    path: 'statement',
                    element: null,
                  },
                ],
              },
            ],
          },
        ],
      },
      {
        path: '*',
        element: (
          <Suspense fallback={<Loader />}>
            <NotFoundPage />
          </Suspense>
        ),
      },
    ],
  },
];
